import React, { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate, useBlocker } from 'react-router-dom';

import { Button, Text, Modal, Banner, Checkbox } from '@shopify/polaris';

import BatchGrid from '../../partials/BatchGrid';
import LoadingSpinner from '../../partials/LoadingSpinner';
import { useGetBatchQuery, useStopPickingMutation, useMarkPickedMutation, useParkPickingMutation } from '../../../warehouse/store/batches';
import { sortItemsByBpn } from '../../../warehouse/utils/bpn';
import { isAuthError, SESSION_EXPIRED_MESSAGE } from '../../../warehouse/utils/errors';
import { useRequirePermission } from '../../../warehouse/utils/permissions';
import { buttonProps, useDocumentTitle } from '../../../warehouse/utils/a11y';
import { UI_COLORS } from '../../../constants';

const PickBatch = () => {
  useDocumentTitle('Pick batch');
  const { batchId } = useParams();
  const navigate = useNavigate();
  // Redirects to Profile when the user lacks the picker permission
  const { loaded: permissionsLoaded, allowed } = useRequirePermission('picker');
  const barcodeInputRef = useRef(null);

  // Load the claimed batch from the backend
  const { currentData: fetchedBatch, isLoading: isLoadingBatch, isFetching: isFetchingBatch, error: batchError, isError: isBatchError } = useGetBatchQuery(batchId, { skip: !batchId });
  const selectedBatch = batchId ? fetchedBatch ?? null : null;

  // Releases the batch back to 'prepared' when the picker stops
  const [stopPicking, { isLoading: isStopping }] = useStopPickingMutation();
  const [stopError, setStopError] = useState('');

  // Marks the batch 'picked' and stores the pick results server-side
  const [markPicked, { isLoading: isMarkingPicked }] = useMarkPickedMutation();
  const [completeError, setCompleteError] = useState('');

  // Parks the batch mid-pick - progress is saved and the claim released
  // so any picker can resume it
  const [parkPicking, { isLoading: isParking }] = useParkPickingMutation();
  const [parkError, setParkError] = useState('');

  // State to track picked items
  const [pickedItems, setPickedItems] = useState(new Set());

  // State to track unpickable items - maps item id to why it can't be
  // picked ('item' = the item itself, 'shipment' = its whole shipment)
  const [unpickableItems, setUnpickableItems] = useState(new Map());

  // State for barcode scanner input
  const [barcodeInput, setBarcodeInput] = useState('');
  const [isBarcodeFocused, setIsBarcodeFocused] = useState(false);

  // State for popup
  const [popupVisible, setPopupVisible] = useState(false);
  const [popupType, setPopupType] = useState(null);
  const [currentItem, setCurrentItem] = useState(null);

  // Queue for multiple items with same SKU
  const [itemQueue, setItemQueue] = useState([]);
  // Total items queued for the current SKU (itemQueue drains as they're confirmed)
  const [skuToteTotal, setSkuToteTotal] = useState(0);

  // State for tote filter (clicked tote in the grid)
  const [selectedTote, setSelectedTote] = useState(null);

  // State for BPN sort - the separator is the store setting that arrives
  // on the batch response, '-' until it (or a missing field) loads
  const [sortDirection, setSortDirection] = useState(null);
  const bpnSeparator = selectedBatch?.bpnSeparator ?? '-';

  // State for BPN filter (set when scanning a non-SKU value)
  const [bpnFilter, setBpnFilter] = useState(null);

  // State for hiding picked items from the list
  const [hidePicked, setHidePicked] = useState(true);

  // Filter/sort controls are collapsed by default - click the toggle row
  // to expand them
  const [filtersExpanded, setFiltersExpanded] = useState(false);

  // Blocks every navigation away (nav links, Back, browser back) while a
  // batch is loaded - the blocker replaces the RN app's tabPress +
  // hardwareBackPress + beforeRemove interception. blocker.state goes to
  // 'blocked' with the attempted location; proceed() resumes it, reset()
  // cancels it. The ref is read at navigation time so performLeave can
  // clear it synchronously before navigating itself - without it that
  // navigate() would be intercepted by this same blocker.
  const allowLeaveRef = useRef(false);
  const blocker = useBlocker(() => !!selectedBatch && !allowLeaveRef.current);

  useEffect(() => {
    if (blocker.state === 'blocked') {
      setStopError('');
      setPopupType('leave-confirm');
      setPopupVisible(true);
    }
  }, [blocker.state]);

  // Resuming a parked batch hydrates the pick state saved in
  // picking_state so the new picker continues where the last one left
  // off. Runs once per batch - later edits are the resuming picker's own.
  const hydratedBatchId = useRef(null);
  useEffect(() => {
    const state = fetchedBatch?.pickingState;
    if (!state || !fetchedBatch || hydratedBatchId.current === fetchedBatch.id) return;
    hydratedBatchId.current = fetchedBatch.id;
    setPickedItems(new Set(state.picked_item_ids || []));
    setUnpickableItems(new Map(Object.entries(state.unpickable_items || {})));
  }, [fetchedBatch]);

  // Manual click on an item asks for confirmation before marking it
  // picked. Clicking an already-picked or unpickable item unmarks it
  // directly.
  const handleItemPress = (item) => {
    if (pickedItems.has(item.id) && unpickableItems.has(item.id)) {
      // Warning state - clicking clears both flags back to unresolved
      setPickedItems(prev => {
        const newSet = new Set(prev);
        newSet.delete(item.id);
        return newSet;
      });
      setUnpickableItems(prev => {
        const newMap = new Map(prev);
        newMap.delete(item.id);
        return newMap;
      });
    } else if (pickedItems.has(item.id)) {
      toggleItemPicked(item.id);
    } else if (unpickableItems.has(item.id)) {
      setUnpickableItems(prev => {
        const newMap = new Map(prev);
        newMap.delete(item.id);
        return newMap;
      });
    } else {
      setCurrentItem(item);
      setPopupType('manual-pick');
      setPopupVisible(true);
    }
  };

  const handleConfirmManualPick = () => {
    if (currentItem) {
      setPickedItems(prev => new Set([...prev, currentItem.id]));
    }
    setPopupVisible(false);
    setPopupType(null);
    setCurrentItem(null);
    setItemQueue([]);
    setTimeout(() => {
      barcodeInputRef.current?.focus();
    }, 100);
  };

  const toggleItemPicked = (itemId) => {
    setPickedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  const handleBarcodeScan = (scannedBarcode) => {
    if (!selectedBatch || !scannedBarcode.trim()) return;

    // Find all items by SKU or UPC (can be multiple items in different totes)
    const scanValue = scannedBarcode.trim();
    const matchingItems = selectedBatch.items.filter(
      i => i.sku === scanValue || i.upc === scanValue
    );

    if (matchingItems.length === 0) {
      // No matching SKU - use the scanned value as a BPN prefix filter.
      // "ataf-" / "atab-" prefixes also set the sort direction.
      let filterValue = scannedBarcode.trim();
      let direction = null;
      if (filterValue.startsWith('ataf-')) {
        filterValue = filterValue.slice('ataf-'.length);
        direction = 'asc';
      } else if (filterValue.startsWith('atab-')) {
        filterValue = filterValue.slice('atab-'.length);
        direction = 'desc';
      }
      const matchedItems = selectedBatch.items.filter(i => (i.bpn || '').startsWith(filterValue));
      if (matchedItems.length === 0) {
        // Nothing matches - show guidance popup, don't apply the filter
        setCurrentItem(null);
        setPopupType('no-match');
        setPopupVisible(true);
      } else {
        setBpnFilter(filterValue);
        // BPN filter replaces the tote filter
        setSelectedTote(null);
        if (direction) setSortDirection(direction);
      }
    } else {
      // Filter out already picked or unpickable items
      const unpickedItems = matchingItems.filter(
        item => !pickedItems.has(item.id) && !unpickableItems.has(item.id)
      );

      if (unpickedItems.length === 0) {
        // All items already picked - show confirmation popup for first one
        setCurrentItem(matchingItems[0]);
        setPopupType('already-picked');
        setPopupVisible(true);
      } else {
        // Add unpicked items to queue
        setItemQueue(unpickedItems);
        setSkuToteTotal(unpickedItems.length);
        // Show popup for first item in queue
        setCurrentItem(unpickedItems[0]);
        setPopupType('new-item');
        setPopupVisible(true);
      }
    }

    // Clear the input after scanning
    setBarcodeInput('');
  };

  const processNextInQueue = (extraResolved) => {
    const nextQueue = itemQueue
      .slice(1)
      .filter(
        i =>
          !pickedItems.has(i.id) &&
          !unpickableItems.has(i.id) &&
          !extraResolved?.has(i.id)
      );
    if (nextQueue.length > 0) {
      // Show next item in queue (queue includes the current item)
      setCurrentItem(nextQueue[0]);
      setItemQueue(nextQueue);
      setPopupType('new-item');
      setPopupVisible(true);
    } else {
      // No more items, clear queue and focus input
      setCurrentItem(null);
      setPopupType(null);
      setPopupVisible(false);
      setItemQueue([]);
      setTimeout(() => {
        barcodeInputRef.current?.focus();
      }, 100);
    }
  };

  const handlePopupDismiss = () => {
    // For new item popups, default to marking as picked when dismissed
    if (popupType === 'new-item' && currentItem) {
      setPickedItems(prev => new Set([...prev, currentItem.id]));
      processNextInQueue();
    } else if (popupType === 'shipment-unpickable') {
      // Treat dismissal as "only this item" - continue the SKU queue
      processNextInQueue();
    } else {
      // For other popup types, just dismiss
      setPopupVisible(false);
      setPopupType(null);
      setCurrentItem(null);
      setItemQueue([]);
      // Dismissing the leave-confirm cancels the blocked navigation
      if (blocker.state === 'blocked') {
        blocker.reset();
      }
      // Focus the input field after popup is dismissed
      setTimeout(() => {
        barcodeInputRef.current?.focus();
      }, 100);
    }
  };

  const handleMarkAsPicked = () => {
    if (currentItem) {
      setPickedItems(prev => new Set([...prev, currentItem.id]));
      processNextInQueue();
    }
  };

  const handleMarkAsUnpickable = () => {
    // Mark the item unpickable instead of picked, then ask if the whole
    // shipment is unpickable. The SKU queue continues after that decision.
    if (currentItem) {
      setUnpickableItems(prev => new Map(prev).set(currentItem.id, 'item'));
      setPopupType('shipment-unpickable');
      setPopupVisible(true);
    }
  };

  const handleMarkShipmentUnpickable = () => {
    // Mark every item in the current item's shipment unpickable, then
    // continue to the next tote that needs the scanned SKU. Items already
    // marked 'item' keep that reason - the item was unpickable on its own.
    const resolved = new Set(unpickableItems.keys());
    if (selectedBatch && currentItem) {
      const shipmentItemIds = selectedBatch.items
        .filter(i => i.orderShipmentId === currentItem.orderShipmentId)
        .map(i => i.id);
      shipmentItemIds.forEach(id => resolved.add(id));
      setUnpickableItems(prev => {
        const newMap = new Map(prev);
        shipmentItemIds.forEach(id => {
          if (!newMap.has(id)) newMap.set(id, 'shipment');
        });
        return newMap;
      });
    }
    processNextInQueue(resolved);
  };

  const handleConfirmPick = () => {
    // For already picked items, just confirm and dismiss
    setPopupVisible(false);
    setPopupType(null);
    setCurrentItem(null);
    setItemQueue([]);
    // Focus the input field after popup is dismissed
    setTimeout(() => {
      barcodeInputRef.current?.focus();
    }, 100);
  };

  // Resolved = picked OR unpickable. Count unique items, not set sizes -
  // an item can be in both sets if its shipment is marked unpickable after
  // it was picked
  const resolvedCount = selectedBatch
    ? selectedBatch.items.filter(
        i => pickedItems.has(i.id) || unpickableItems.has(i.id)
      ).length
    : 0;
  const allPicked = selectedBatch
    ? selectedBatch.items.length > 0 && resolvedCount >= selectedBatch.items.length
    : false;

  // Auto-focus the barcode input after 1s of inactivity so it's always
  // ready for the next scan (unless a popup is open or picking is done).
  useEffect(() => {
    if (!selectedBatch) return;
    if (isBarcodeFocused || popupVisible) return;
    if (allPicked) return;
    const timer = setTimeout(() => {
      barcodeInputRef.current?.focus();
    }, 1000);
    return () => clearTimeout(timer);
  }, [selectedBatch, isBarcodeFocused, popupVisible, pickedItems, allPicked]);

  // When the batch is fully picked, unfocus the barcode input
  useEffect(() => {
    if (allPicked) barcodeInputRef.current?.blur();
  }, [allPicked]);

  // Clears local state and resumes the blocked navigation - or returns
  // to the picking queue when no navigation was pending (complete/park
  // flows initiated from buttons)
  const performLeave = () => {
    setPopupVisible(false);
    setPopupType(null);
    setCurrentItem(null);
    setItemQueue([]);
    // Clear known picked items before leaving
    setPickedItems(new Set());
    setUnpickableItems(new Map());
    allowLeaveRef.current = true;
    if (blocker.state === 'blocked') {
      blocker.proceed();
    } else {
      navigate('/warehouse/app/picking');
    }
  };

  // 'Stop Picking' tells the backend to return the batch to 'prepared' and
  // release this picker's claim before navigating away. If the release
  // fails the popup stays open with an error so they can retry.
  const handleStopPicking = async () => {
    if (!selectedBatch || isStopping) return;

    const result = await stopPicking(selectedBatch.id);

    if ('error' in result) {
      const status = result.error?.status;
      setStopError(
        status === 401
          ? SESSION_EXPIRED_MESSAGE
          : status === 403
            ? 'This batch is being picked by someone else.'
            : status === 409
              ? 'This batch is no longer being picked.'
              : 'Could not release the batch. Check your connection and try again.'
      );
      return;
    }

    setStopError('');
    performLeave();
  };

  // Confirming the complete-pick popup marks the batch 'picked' on the
  // backend, along with the cart's per-item pick state (picked ids and
  // unpickable reasons) so the backend knows which totes packing can use.
  const handleCompletePickConfirmed = async () => {
    if (!selectedBatch || isMarkingPicked) return;

    const result = await markPicked({
      batchId: selectedBatch.id,
      pickedItemIds: Array.from(pickedItems),
      unpickableItems: Object.fromEntries(unpickableItems),
    });

    if ('error' in result) {
      const status = result.error?.status;
      setCompleteError(
        status === 401
          ? SESSION_EXPIRED_MESSAGE
          : status === 403
            ? 'This batch is being picked by someone else.'
            : status === 409
              ? 'This batch is no longer being picked.'
              : status === 422
                ? 'Every item must be marked picked or unpickable before the batch can be completed.'
                : 'Could not mark the batch picked. Check your connection and try again.'
      );
      return;
    }

    setCompleteError('');
    performLeave();
  };

  // 'Park Batch' asks for confirmation, then saves the current pick
  // progress and releases the claim so any picker can resume the batch.
  // Called from the header or the leave guard - a blocked navigation is
  // resumed after parking so it still takes the user where they headed
  const handleParkBatch = () => {
    setParkError('');
    setPopupType('park-pick');
    setPopupVisible(true);
  };

  const handleParkPickConfirmed = async () => {
    if (!selectedBatch || isParking) return;

    const result = await parkPicking({
      batchId: selectedBatch.id,
      pickedItemIds: Array.from(pickedItems),
      unpickableItems: Object.fromEntries(unpickableItems),
    });

    if ('error' in result) {
      const status = result.error?.status;
      setParkError(
        status === 401
          ? SESSION_EXPIRED_MESSAGE
          : status === 403
            ? 'This batch is being picked by someone else.'
            : status === 409
              ? 'This batch is no longer being picked.'
              : 'Could not park the batch. Check your connection and try again.'
      );
      return;
    }

    setParkError('');
    performLeave();
  };

  // Confirming completion navigates back to the batch list
  const handleMarkBatchPicked = () => {
    setCompleteError('');
    setPopupType('complete-pick');
    setPopupVisible(true);
  };

  const handleToteFilterPress = (tote) => {
    setSelectedTote(prev =>
      prev && prev.row === tote.row && prev.column === tote.column ? null : { row: tote.row, column: tote.column }
    );
  };

  // Progress counts unique resolved (picked or unpickable) items
  const progress = resolvedCount;
  const totalItems = selectedBatch ? selectedBatch.items.length : 0;

  const filteredItems = selectedBatch
    ? selectedBatch.items.filter(
        i =>
          (!selectedTote || (i.row === selectedTote.row && i.column === selectedTote.column)) &&
          (!bpnFilter || (i.bpn || '').startsWith(bpnFilter)) &&
          (!hidePicked || (!pickedItems.has(i.id) && !unpickableItems.has(i.id)))
      )
    : [];

  const visibleItems = sortDirection
    ? sortItemsByBpn(filteredItems, bpnSeparator, sortDirection)
    : filteredItems;

  const renderToteGrid = (row, column, toteSize = 16) => (
    <BatchGrid
      totalRows={selectedBatch?.totalRows ?? 0}
      totalColumns={selectedBatch?.totalColumns ?? 0}
      toteSize={toteSize}
      gap={3}
      highlightedTotes={[{ row, column, color: UI_COLORS.cyan }]}
    />
  );

  const renderItem = (item) => {
    const isPicked = pickedItems.has(item.id);
    const isUnpickable = unpickableItems.has(item.id);
    // Both flags = item was picked, then its shipment was marked unpickable
    const isWarning = isPicked && isUnpickable;
    const unpickableReason = unpickableItems.get(item.id);

    const cardClasses = [
      'warehouse-item-card',
      isPicked ? 'warehouse-item-picked' : '',
      isUnpickable ? 'warehouse-item-unpickable' : '',
      isWarning ? 'warehouse-item-warning' : '',
    ].filter(Boolean).join(' ');

    const badgeClasses = [
      'warehouse-item-badge',
      isPicked || isUnpickable ? 'warehouse-item-badge-active' : '',
      isUnpickable ? 'warehouse-item-badge-unpickable' : '',
      isWarning ? 'warehouse-item-badge-warning' : '',
    ].filter(Boolean).join(' ');

    const badgeContent = isWarning
      ? <><span aria-hidden="true">!</span><span className="visually-hidden">Picked, but shipment is unpickable</span></>
      : isPicked
        ? <><span aria-hidden="true">✓</span><span className="visually-hidden">Picked</span></>
        : isUnpickable
          ? <><span aria-hidden="true">✕</span><span className="visually-hidden">Unpickable</span></>
          : '';

    return (
      <div key={item.id} className={cardClasses} {...buttonProps(() => handleItemPress(item))}>
        <div className="warehouse-item-header">
          <span className="warehouse-item-name">{item.name}</span>
          <span className={badgeClasses}>{badgeContent}</span>
        </div>

        {item.sku && (
          <div className="warehouse-item-info">
            <span className="warehouse-info-label">SKU:</span>
            <span>{item.sku}</span>
          </div>
        )}

        {item.upc && (
          <div className="warehouse-item-info">
            <span className="warehouse-info-label">UPC:</span>
            <span>{item.upc}</span>
          </div>
        )}

        {item.variantOptions && item.variantOptions.length > 0 && (
          <div className="warehouse-item-info">
            <span className="warehouse-info-label">Variant:</span>
            <span>{item.variantOptions.map(o => `${o.option_display_name}: ${o.label}`).join(', ')}</span>
          </div>
        )}

        <div className="warehouse-item-info">
          <span className="warehouse-info-label">BPN:</span>
          <span>{item.bpn}</span>
        </div>

        <div className="warehouse-item-info">
          <span className="warehouse-info-label">Qty:</span>
          <span>{item.quantity}</span>
        </div>

        <div className="warehouse-item-info">
          <span className="warehouse-info-label">Shipment:</span>
          <span>{item.orderShipmentId}</span>
        </div>

        {isUnpickable && (
          <div className="warehouse-item-info">
            <span className="warehouse-info-label">Status:</span>
            <span className="warehouse-unpickable-text">
              {unpickableReason === 'shipment'
                ? `Unpickable - shipment ${item.orderShipmentId}`
                : 'Unpickable - item'}
            </span>
          </div>
        )}

        <div className="warehouse-item-info">
          <span className="warehouse-info-label">Tote:</span>
          {renderToteGrid(item.row, item.column)}
        </div>
      </div>
    );
  };

  if (!permissionsLoaded || !allowed) {
    return null;
  }

  return (
    <div className="warehouse-screen">
      <div className="warehouse-header">
        <div className="warehouse-header-row">
          <div>
            <h2>Pick Batch</h2>
            <div className="warehouse-header-subtitle">
              {selectedBatch ? selectedBatch.name : 'Active picking sessions'}
            </div>
            {selectedBatch?.cartId ? (
              <div className="warehouse-header-cart">Cart: {selectedBatch.cartId}</div>
            ) : null}
          </div>
          {selectedBatch && (
            <Button onClick={handleParkBatch}>Park Batch</Button>
          )}
        </div>
      </div>

      {selectedBatch ? (
        <>
          <div className="warehouse-progress">
            <div
              className="warehouse-progress-bar"
              role="progressbar"
              aria-valuenow={progress}
              aria-valuemin={0}
              aria-valuemax={totalItems}
              aria-label="Pick progress"
            >
              <div
                className="warehouse-progress-fill"
                style={{ width: `${totalItems ? (progress / totalItems) * 100 : 0}%` }}
              />
            </div>
            <div className="warehouse-progress-text">
              {progress} / {totalItems} items
            </div>
          </div>

          {!allPicked && (
            <div className="warehouse-scanner">
              <div className={`warehouse-scan-indicator${isBarcodeFocused ? ' warehouse-scan-indicator-ready' : ''}`} role="status">
                {isBarcodeFocused ? 'Ready to scan' : 'Not ready to scan'}
              </div>
              <input
                ref={barcodeInputRef}
                className={`warehouse-barcode-input${isBarcodeFocused ? ' warehouse-barcode-input-focused' : ''}`}
                value={barcodeInput}
                onChange={(e) => setBarcodeInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleBarcodeScan(e.target.value); }}
                onFocus={() => setIsBarcodeFocused(true)}
                onBlur={() => setIsBarcodeFocused(false)}
                placeholder="Click to focus, then scan barcode"
                aria-label="Barcode input - focus, then scan barcode"
                autoCapitalize="none"
                autoCorrect="off"
              />
            </div>
          )}

          <div className="warehouse-filters">
            {!allPicked && (
              <button
                type="button"
                className="warehouse-filter-toggle"
                onClick={() => setFiltersExpanded(prev => !prev)}
                aria-expanded={filtersExpanded}
              >
                <span><span aria-hidden="true">{filtersExpanded ? '▾' : '▸'}</span> Filters & Sort</span>
                {!filtersExpanded && (
                  <span className="warehouse-filter-summary">
                    {[
                      selectedTote && `TOTE-${selectedTote.row}-${selectedTote.column}`,
                      bpnFilter && `BPN: ${bpnFilter}`,
                      sortDirection && (sortDirection === 'asc' ? 'BPN ↑' : 'BPN ↓'),
                      hidePicked && 'Picked hidden',
                    ].filter(Boolean).join('  ·  ')}
                  </span>
                )}
              </button>
            )}
            {(allPicked || filtersExpanded) && (
              <>
                {!allPicked && (
                  <>
                    {!bpnFilter && (
                      <>
                        <div className="warehouse-filter-label">
                          {selectedTote
                            ? `Showing TOTE-${selectedTote.row}-${selectedTote.column} — click again to clear`
                            : 'Click a tote to filter items'}
                        </div>
                        <BatchGrid
                          totalRows={selectedBatch.totalRows}
                          totalColumns={selectedBatch.totalColumns}
                          toteCount={selectedBatch.toteCount}
                          toteSize={52}
                          gap={8}
                          highlightedTotes={
                            selectedTote
                              ? [{ row: selectedTote.row, column: selectedTote.column, color: UI_COLORS.header }]
                              : []
                          }
                          onTotePress={handleToteFilterPress}
                          pressedTotes={selectedTote ? [selectedTote] : []}
                          ariaLabel="Filter items by tote"
                        />
                      </>
                    )}
                    <div className="warehouse-sort-row" role="group" aria-label="Sort by BPN">
                      <span className="warehouse-info-label">Sort by BPN:</span>
                      <button
                        type="button"
                        className={`warehouse-sort-chip${sortDirection === 'asc' ? ' warehouse-sort-chip-active' : ''}`}
                        onClick={() => setSortDirection(prev => (prev === 'asc' ? null : 'asc'))}
                        aria-pressed={sortDirection === 'asc'}
                      >
                        Ascending
                      </button>
                      <button
                        type="button"
                        className={`warehouse-sort-chip${sortDirection === 'desc' ? ' warehouse-sort-chip-active' : ''}`}
                        onClick={() => setSortDirection(prev => (prev === 'desc' ? null : 'desc'))}
                        aria-pressed={sortDirection === 'desc'}
                      >
                        Descending
                      </button>
                    </div>
                  </>
                )}
                <div className="warehouse-switch-row">
                  <span className="warehouse-info-label">Hide picked items</span>
                  <Checkbox checked={hidePicked} onChange={setHidePicked} label="Hide picked items" labelHidden />
                </div>
              </>
            )}
          </div>

          {!allPicked && bpnFilter && (
            <div className="warehouse-bpn-filter-banner">
              <span>Filtering by BPN: {bpnFilter}</span>
              <button type="button" onClick={() => setBpnFilter(null)} aria-label="Clear BPN filter">Clear</button>
            </div>
          )}

          {allPicked && (
            <div className="warehouse-complete">
              <div className="warehouse-complete-title">✓ All items picked</div>
              <Text as="p">
                All {totalItems} items in this batch have been picked.
              </Text>
              <Button variant="primary" onClick={handleMarkBatchPicked}>
                Mark Batch Picked
              </Button>
            </div>
          )}

          <div className="warehouse-list">
            {visibleItems.map(renderItem)}
          </div>
        </>
      ) : batchId && (isLoadingBatch || (isFetchingBatch && !selectedBatch)) ? (
        <div className="warehouse-loading"><LoadingSpinner statusText="Loading batch..." /></div>
      ) : batchId && (isBatchError || !selectedBatch) ? (
        <div className="warehouse-empty">
          <h2>{isAuthError(batchError) ? 'Session expired' : 'Couldn’t load batch'}</h2>
          <Text as="p">
            {isAuthError(batchError)
              ? SESSION_EXPIRED_MESSAGE
              : 'The batch could not be loaded from the server. It may have been unpacked or claimed by another picker.'}
          </Text>
        </div>
      ) : (
        <div className="warehouse-empty">
          <h2>No Active Picks</h2>
          <Text as="p">Start picking from the Picking queue</Text>
        </div>
      )}

      <Modal
        open={popupVisible}
        onClose={handlePopupDismiss}
      >
        <Modal.Section>
        {popupType === 'new-item' && currentItem && (
          <>
            <h2>✓ Item Found</h2>
            <Text as="p" fontWeight="bold">{currentItem.name}</Text>
            <div className="warehouse-item-info">
              <span className="warehouse-info-label">Quantity:</span>
              <span>{currentItem.quantity}</span>
            </div>
            <div className="warehouse-item-info">
              <span className="warehouse-info-label">BPN:</span>
              <span>{currentItem.bpn}</span>
            </div>
            <div className="warehouse-item-info">
              <span className="warehouse-info-label">Tote:</span>
              {renderToteGrid(currentItem.row, currentItem.column, 18)}
            </div>
            {skuToteTotal > 1 && (
              <Text as="p">
                {skuToteTotal - itemQueue.length + 1} of {skuToteTotal} totes with this item
              </Text>
            )}
            <div className="warehouse-popup-buttons">
              <Button variant="primary" onClick={handleMarkAsPicked}>Mark as Picked</Button>
              <Button variant="primary" tone="critical" onClick={handleMarkAsUnpickable}>Mark Unpickable</Button>
            </div>
          </>
        )}

        {popupType === 'already-picked' && currentItem && (
          <>
            <h2>⚠ Already Picked</h2>
            <Text as="p" fontWeight="bold">{currentItem.name}</Text>
            <div className="warehouse-item-info">
              <span className="warehouse-info-label">Quantity:</span>
              <span>{currentItem.quantity}</span>
            </div>
            <div className="warehouse-item-info">
              <span className="warehouse-info-label">BPN:</span>
              <span>{currentItem.bpn}</span>
            </div>
            <div className="warehouse-item-info">
              <span className="warehouse-info-label">Tote:</span>
              {renderToteGrid(currentItem.row, currentItem.column, 18)}
            </div>
            <div className="warehouse-popup-buttons">
              <Button variant="primary" onClick={handleConfirmPick}>Confirm</Button>
            </div>
          </>
        )}

        {popupType === 'manual-pick' && currentItem && (
          <>
            <h2>Confirm Pick</h2>
            <Text as="p" fontWeight="bold">{currentItem.name}</Text>
            <div className="warehouse-item-info">
              <span className="warehouse-info-label">Quantity:</span>
              <span>{currentItem.quantity}</span>
            </div>
            <div className="warehouse-item-info">
              <span className="warehouse-info-label">BPN:</span>
              <span>{currentItem.bpn}</span>
            </div>
            <div className="warehouse-item-info">
              <span className="warehouse-info-label">Tote:</span>
              {renderToteGrid(currentItem.row, currentItem.column, 18)}
            </div>
            <div className="warehouse-popup-buttons">
              <Button variant="primary" onClick={handleConfirmManualPick}>Confirm Picked</Button>
              <Button onClick={handlePopupDismiss}>Cancel</Button>
              <Button variant="primary" tone="critical" onClick={handleMarkAsUnpickable}>Mark Unpickable</Button>
            </div>
          </>
        )}

        {popupType === 'shipment-unpickable' && currentItem && (
          <>
            <h2>Mark Shipment Unpickable?</h2>
            <Text as="p">
              Should the entire shipment {currentItem.orderShipmentId} be marked as unpickable?
            </Text>
            <div className="warehouse-popup-buttons">
              <Button variant="primary" onClick={() => processNextInQueue()}>Only This Item</Button>
              <Button variant="primary" tone="critical" onClick={handleMarkShipmentUnpickable}>Entire Shipment</Button>
            </div>
          </>
        )}

        {popupType === 'leave-confirm' && (
          <>
            <h2>Leave This Batch?</h2>
            <Text as="p">
              Parking saves your progress so any picker can resume this batch. Stopping discards your progress and returns it to the picking queue.
            </Text>
            {stopError ? (
              <Banner tone="critical"><p>{stopError}</p></Banner>
            ) : null}
            {isStopping ? (
              <div className="warehouse-loading"><LoadingSpinner statusText="Releasing batch..." /></div>
            ) : (
              <div className="warehouse-popup-buttons">
                <Button variant="primary" onClick={handlePopupDismiss}>Keep Picking</Button>
                <Button onClick={handleParkBatch}>Park Batch</Button>
                <Button variant="primary" tone="critical" onClick={handleStopPicking}>Stop Picking</Button>
              </div>
            )}
          </>
        )}

        {popupType === 'complete-pick' && (
          <>
            <h2>Complete Picking?</h2>
            <Text as="p">
              Confirm that picking is complete and the cart has been dropped off for packing.
            </Text>
            {completeError ? (
              <Banner tone="critical"><p>{completeError}</p></Banner>
            ) : null}
            {isMarkingPicked ? (
              <div className="warehouse-loading"><LoadingSpinner statusText="Marking batch picked..." /></div>
            ) : (
              <div className="warehouse-popup-buttons">
                <Button onClick={handlePopupDismiss}>Cancel</Button>
                <Button variant="primary" onClick={handleCompletePickConfirmed}>Complete</Button>
              </div>
            )}
          </>
        )}

        {popupType === 'park-pick' && (
          <>
            <h2>Park Batch?</h2>
            <Text as="p">
              Parking saves your progress and releases this batch so any picker can resume it later.
            </Text>
            {parkError ? (
              <Banner tone="critical"><p>{parkError}</p></Banner>
            ) : null}
            {isParking ? (
              <div className="warehouse-loading"><LoadingSpinner statusText="Parking batch..." /></div>
            ) : (
              <div className="warehouse-popup-buttons">
                <Button onClick={handlePopupDismiss}>Cancel</Button>
                <Button variant="primary" onClick={handleParkPickConfirmed}>Park Batch</Button>
              </div>
            )}
          </>
        )}

        {popupType === 'no-match' && (
          <>
            <h2>No Match Found</h2>
            <Text as="p">
              No matching SKU or UPC was found, and no item BPNs match the scanned value.
            </Text>
            <Text as="p">
              If you were scanning an area, you can move on to the next area.
            </Text>
            <Text as="p">
              If you were scanning a product, the item should not be picked.
            </Text>
            <div className="warehouse-popup-buttons">
              <Button variant="primary" onClick={handlePopupDismiss}>OK</Button>
            </div>
          </>
        )}
        </Modal.Section>
      </Modal>
    </div>
  );
};

export default PickBatch;
